import {
  findClientConfig,
  getCityConfigEntry,
  resolveUploadPath,
} from "../services/companyFileReader";
import { getClientFileUrl } from "../services/uploads";
import { getCitiesToPrint } from "./reportShared";

/**
 * Slide background image keys (filename stem, without extension).
 *
 * Upload layout on the Upload API (under mystery_client/):
 *
 *   {year}/{clientId}/report/
 *     cover.jpg                    ← client-wide (used for "Tutte" cover)
 *     divider.jpg                  ← default section divider
 *     divider_executive.jpg        ← executive summary divider (optional)
 *     divider_punti_vendita.jpg
 *     divider_fermate.jpg
 *     divider_linee.jpg
 *     divider_call_center.jpg
 *     divider_web_app.jpg
 *
 *   {year}/{clientId}/report/{city_slug}/
 *     cover.jpg                    ← per-city cover (single-city export)
 *     divider.jpg                  ← per-city default divider
 *     divider_executive.jpg
 *     … (same optional section-specific files)
 *
 * city_slug = lowercase city from clients.json, spaces → underscores
 *   e.g. MESSINA → messina, REGGIO EMILIA → reggio_emilia
 *
 * Optional explicit paths in clients.json (overrides auto paths):
 *   "cities": {
 *     "MESSINA": {
 *       "reportImages": {
 *         "cover": "report/messina/cover.jpg",
 *         "divider_linee": "report/messina/divider_linee.jpg"
 *       }
 *     }
 *   }
 *
 * Supported formats: .jpg, .jpeg, .png, .webp
 */

export const REPORT_IMAGE_KEYS = {
  cover: "cover",
  divider: "divider",
  divider_executive: "divider_executive",
  divider_punti_vendita: "divider_punti_vendita",
  divider_fermate: "divider_fermate",
  divider_linee: "divider_linee",
  divider_call_center: "divider_call_center",
  divider_web_app: "divider_web_app",
};

export const DIVIDER_IMAGE_BY_LABEL = {
  "PUNTI VENDITA": REPORT_IMAGE_KEYS.divider_punti_vendita,
  FERMATE: REPORT_IMAGE_KEYS.divider_fermate,
  LINEE: REPORT_IMAGE_KEYS.divider_linee,
  "CALL CENTER": REPORT_IMAGE_KEYS.divider_call_center,
  "WEB E APP": REPORT_IMAGE_KEYS.divider_web_app,
};

const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "webp"];

export function cityToReportSlug(cityName) {
  if (!cityName) return "";
  return cityName
    .toLowerCase()
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "_")
    .replace(/[^a-z0-9_]/g, "");
}

function authHeaders() {
  const token = localStorage.getItem("auth_token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function isRasterDataUrl(dataUrl) {
  return typeof dataUrl === "string"
    && (dataUrl.startsWith("data:image/png")
      || dataUrl.startsWith("data:image/jpeg")
      || dataUrl.startsWith("data:image/jpg")
      || dataUrl.startsWith("data:image/webp"));
}

export async function fetchReportImageDataUrl(filePath) {
  if (!filePath) return null;
  try {
    const url = getClientFileUrl(filePath);
    const res = await fetch(url, { headers: authHeaders() });
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob.type.startsWith("image/")) return null;
    const dataUrl = await blobToDataUrl(blob);
    return isRasterDataUrl(dataUrl) ? dataUrl : null;
  } catch {
    return null;
  }
}

export function getReportImageCandidates(clientConf, year, cityName, imageKey) {
  if (!clientConf?.clientId || !year || !imageKey) return [];

  const clientId = clientConf.clientId;
  const paths = [];
  const slug = cityToReportSlug(cityName);

  const cityEntry = cityName ? getCityConfigEntry(clientConf, cityName) : null;
  const configured = cityEntry?.reportImages?.[imageKey];
  if (configured) {
    paths.push(resolveUploadPath(clientConf, configured, year));
  }

  const clientConfigured = clientConf.reportImages?.[imageKey];
  if (clientConfigured) {
    paths.push(resolveUploadPath(clientConf, clientConfigured, year));
  }

  if (slug) {
    for (const ext of IMAGE_EXTENSIONS) {
      paths.push(`${year}/${clientId}/report/${slug}/${imageKey}.${ext}`);
    }
  }

  for (const ext of IMAGE_EXTENSIONS) {
    paths.push(`${year}/${clientId}/report/${imageKey}.${ext}`);
  }

  return [...new Set(paths.filter(Boolean))];
}

export async function loadReportSlideImage(clientConf, year, cityName, imageKey) {
  const candidates = getReportImageCandidates(clientConf, year, cityName, imageKey);
  for (const path of candidates) {
    const dataUrl = await fetchReportImageDataUrl(path);
    if (dataUrl) return dataUrl;
  }
  return null;
}

/**
 * Preload all slide backgrounds needed for a report export.
 * @returns {{ cover: string|null, client: Object, byCity: Object }}
 */
export async function loadReportSlideImages(company, selectedCity) {
  const clientConf = findClientConfig(company.azienda);
  const year = company.anno;
  const cities = getCitiesToPrint(company, selectedCity);
  const isTutte = !selectedCity && cities.length > 1;

  const result = {
    isTutte,
    cities,
    client: {},
    byCity: {},
    cover: null,
  };

  if (!clientConf || !year) return result;

  const clientKeys = [
    REPORT_IMAGE_KEYS.cover,
    REPORT_IMAGE_KEYS.divider,
    REPORT_IMAGE_KEYS.divider_executive,
    ...Object.values(DIVIDER_IMAGE_BY_LABEL),
  ];

  const clientLoads = clientKeys.map(async (key) => {
    result.client[key] = await loadReportSlideImage(clientConf, year, null, key);
  });
  await Promise.all(clientLoads);

  const cityLoads = cities.map(async (city) => {
    const entry = {};
    const keys = [...new Set([...clientKeys, REPORT_IMAGE_KEYS.cover])];
    await Promise.all(keys.map(async (key) => {
      entry[key] = await loadReportSlideImage(clientConf, year, city, key);
    }));
    result.byCity[city] = entry;
  });
  await Promise.all(cityLoads);

  if (isTutte) {
    result.cover = result.client[REPORT_IMAGE_KEYS.cover] || null;
  } else if (cities.length === 1) {
    const city = cities[0];
    result.cover = result.byCity[city]?.[REPORT_IMAGE_KEYS.cover]
      || result.client[REPORT_IMAGE_KEYS.cover]
      || null;
  } else {
    result.cover = result.client[REPORT_IMAGE_KEYS.cover] || null;
  }

  return result;
}

export function getDividerBackground(slideImages, city, dividerLabel) {
  const sectionKey = DIVIDER_IMAGE_BY_LABEL[dividerLabel];
  const cityEntry = slideImages.byCity[city] || {};
  const client = slideImages.client || {};

  if (dividerLabel === "EXECUTIVE") {
    return cityEntry[REPORT_IMAGE_KEYS.divider_executive]
      || cityEntry[REPORT_IMAGE_KEYS.divider]
      || client[REPORT_IMAGE_KEYS.divider_executive]
      || client[REPORT_IMAGE_KEYS.divider]
      || null;
  }

  if (sectionKey) {
    return cityEntry[sectionKey]
      || client[sectionKey]
      || cityEntry[REPORT_IMAGE_KEYS.divider]
      || client[REPORT_IMAGE_KEYS.divider]
      || null;
  }

  return cityEntry[REPORT_IMAGE_KEYS.divider]
    || client[REPORT_IMAGE_KEYS.divider]
    || null;
}

export function getCoverBackground(slideImages, city) {
  if (slideImages.isTutte) return slideImages.cover;
  if (city && slideImages.byCity[city]?.[REPORT_IMAGE_KEYS.cover]) {
    return slideImages.byCity[city][REPORT_IMAGE_KEYS.cover];
  }
  return slideImages.cover;
}
